import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Dices } from 'lucide-react';
import {
  VIDEO_ASPECT_RATIO_OPTIONS,
  VIDEO_DEFAULT_ASPECT_RATIO,
  VIDEO_DEFAULT_DURATION_SEC,
  VIDEO_DEFAULT_GENERATION_MODE,
  VIDEO_DEFAULT_SEED,
  VIDEO_DURATION_MAX,
  VIDEO_DURATION_MIN,
  VIDEO_DURATION_PRESETS,
  VIDEO_GENERATION_MODE_OPTIONS,
  VIDEO_SEED_MAX,
  VIDEO_SEED_MIN,
  VIDEO_T2V_PROMPT_MAX_LENGTH,
  createRandomVideoSeed,
  getVideoAspectRatioFrameStyle,
  normalizeVideoAspectRatio,
  normalizeVideoDurationSec,
  normalizeVideoGenerationMode,
  normalizeVideoSeed,
} from '@/pages/ipe/workbench/constants/videoStudioConfig';
import useStudioSidebarDockLeft from '@/pages/ipe/workbench/hooks/useStudioSidebarDockLeft';
import { useHistoryPanelTransition } from '@/pages/ipe/workbench/hooks/useHistoryPanelTransition';
import { resolveLockedTitlePresetScope } from '@/pages/ipe/workbench/constants/titlePresetCatalog';
import { useIpeWorkspaceMode } from '@/pages/ipe/workbench/hooks/useIpeWorkspaceMode';
import '@/styles/ipe/workbench/modals/Text2ImgPromptCaseFormModal.css';

function emptyForm(scope = 'personal', defaults = {}) {
  return {
    name: String(defaults.name || ''),
    prompt: String(defaults.prompt || ''),
    generationMode: normalizeVideoGenerationMode(
      defaults.generationMode || VIDEO_DEFAULT_GENERATION_MODE,
    ),
    aspectRatio: normalizeVideoAspectRatio(defaults.aspectRatio || VIDEO_DEFAULT_ASPECT_RATIO),
    durationSec: normalizeVideoDurationSec(defaults.durationSec ?? VIDEO_DEFAULT_DURATION_SEC),
    seed: normalizeVideoSeed(defaults.seed ?? VIDEO_DEFAULT_SEED),
    caseScope: scope === 'team' ? 'team' : 'personal',
  };
}

function formFromCase(item, fallbackScope = 'personal') {
  if (!item) return emptyForm(fallbackScope);
  return {
    name: String(item.name || ''),
    prompt: String(item.prompt || ''),
    generationMode: normalizeVideoGenerationMode(item.generationMode || VIDEO_DEFAULT_GENERATION_MODE),
    aspectRatio: normalizeVideoAspectRatio(item.aspectRatio || VIDEO_DEFAULT_ASPECT_RATIO),
    durationSec: normalizeVideoDurationSec(item.durationSec ?? VIDEO_DEFAULT_DURATION_SEC),
    seed: normalizeVideoSeed(item.seed ?? VIDEO_DEFAULT_SEED),
    caseScope: item.scope === 'team' ? 'team' : 'personal',
  };
}

export function validateVideoT2vPromptCaseForm(form, t) {
  const tr = typeof t === 'function' ? t : (key, opts) => (opts?.defaultValue ?? key);
  const name = String(form?.name || '').trim();
  const prompt = String(form?.prompt || '').trim();
  if (!name) return tr('promptCase.errNameRequired');
  if (!prompt) return tr('promptCase.errT2vPromptRequired');
  if (prompt.length > VIDEO_T2V_PROMPT_MAX_LENGTH) {
    return tr('promptCase.errT2vPromptTooLong', { max: VIDEO_T2V_PROMPT_MAX_LENGTH });
  }
  const mode = normalizeVideoGenerationMode(form?.generationMode);
  if (!VIDEO_GENERATION_MODE_OPTIONS.some((item) => item.value === mode)) {
    return tr('promptCase.errQualityRequired');
  }
  const ratio = normalizeVideoAspectRatio(form?.aspectRatio);
  if (!VIDEO_ASPECT_RATIO_OPTIONS.some((item) => item.value === ratio)) {
    return tr('promptCase.errVideoRatioRequired');
  }
  const duration = normalizeVideoDurationSec(form?.durationSec);
  if (duration < VIDEO_DURATION_MIN || duration > VIDEO_DURATION_MAX) {
    return tr('promptCase.errDurationRange', { min: VIDEO_DURATION_MIN, max: VIDEO_DURATION_MAX });
  }
  const seed = Number(form?.seed);
  if (!Number.isFinite(seed) || seed < VIDEO_SEED_MIN || seed > VIDEO_SEED_MAX) {
    return tr('promptCase.errSeedRange', { min: VIDEO_SEED_MIN, max: VIDEO_SEED_MAX });
  }
  return null;
}

export function buildVideoT2vPromptCasePayload(form) {
  return {
    name: String(form?.name || '').trim(),
    prompt: String(form?.prompt || '').trim(),
    generationMode: normalizeVideoGenerationMode(form?.generationMode),
    aspectRatio: normalizeVideoAspectRatio(form?.aspectRatio),
    durationSec: normalizeVideoDurationSec(form?.durationSec),
    seed: normalizeVideoSeed(form?.seed),
    caseScope: form?.caseScope === 'team' ? 'team' : 'personal',
  };
}

/**
 * 文生视频模板 · 新建 / 编辑表单
 */
export default function VideoT2vPromptCaseFormModal({
  isOpen,
  mode = 'create',
  /** 可选：工作台顶栏模式（不传则自动跟随顶栏）。弹窗内不可切换归属 */
  workspaceMode,
  caseItem = null,
  defaults = null,
  canAccessTeamPresets = false,
  saving = false,
  onClose,
  onSubmit,
  /** 居中弹窗：工作流节点参数面板内使用（功能页默认仍贴左侧参数栏滑入） */
  centered = false,
}) {
  const isEdit = mode === 'edit';
  const { t } = useTranslation('workbench');
  const {
    mounted,
    exiting,
    requestClose,
    handlePanelAnimationEnd,
  } = useHistoryPanelTransition(isOpen, onClose, {
    baseClassName: 't2i-case-form-modal-layer',
    deferContent: false,
    closeNotifiesParent: true,
  });
  const dockLeft = useStudioSidebarDockLeft(mounted && !centered);
  const workspaceModeFromHook = useIpeWorkspaceMode();
  const lockedScope = resolveLockedTitlePresetScope(
    workspaceMode || workspaceModeFromHook,
    canAccessTeamPresets,
  );
  const [form, setForm] = useState(() => emptyForm(lockedScope, defaults || {}));
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) {
      setForm(formFromCase(caseItem, lockedScope));
    } else {
      setForm(emptyForm(lockedScope, defaults || {}));
    }
    setError('');
  }, [isOpen, isEdit, caseItem, lockedScope, defaults]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, saving, requestClose]);

  const promptLen = String(form.prompt || '').length;
  const safeDurationSec = normalizeVideoDurationSec(form.durationSec);
  const safeSeed = normalizeVideoSeed(form.seed);
  const canSubmit = useMemo(() => {
    return Boolean(String(form.name || '').trim() && String(form.prompt || '').trim()) && !saving;
  }, [form.name, form.prompt, saving]);

  if (!mounted) return null;

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (error) setError('');
  };

  const handleSeedInputChange = (e) => {
    const raw = e.target.value;
    if (raw === '') {
      setField('seed', VIDEO_SEED_MIN);
      return;
    }
    const n = Number(raw);
    if (!Number.isFinite(n)) return;
    setField('seed', normalizeVideoSeed(n));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const msg = validateVideoT2vPromptCaseForm(form, t);
    if (msg) {
      setError(msg);
      return;
    }
    const payload = buildVideoT2vPromptCasePayload(form);
    await onSubmit?.(payload, { mode, caseItem });
  };

  const safeClose = () => {
    if (!saving) requestClose();
  };

  return createPortal(
    <div
      className={`funding-modal funding-modal--top t2i-case-form-modal-layer${centered ? ' ipe-case-modal--centered' : ''}${exiting ? ' is-closing' : ' is-opening'}`}
      style={{ '--t2i-case-library-dock-left': `${dockLeft}px` }}
    >
      <div
        className="funding-modal__backdrop"
        role="presentation"
        onClick={safeClose}
      />
      <div
        className="funding-modal__panel funding-modal__panel--unified funding-modal__panel--t2i-case-form"
        role="dialog"
        aria-modal="true"
        aria-labelledby="t2v-case-form-title"
        onAnimationEnd={handlePanelAnimationEnd}
      >
        <header className="funding-modal__header t2i-case-form__header">
          <div className="t2i-case-form__header-text">
            <h3 id="t2v-case-form-title">{isEdit ? t('promptCase.editTemplate') : t('promptCase.newTemplate')}</h3>
            <p className="t2i-case-form__subtitle">
              {isEdit
                ? t('promptCase.syncHint')
                : t('promptCase.t2vSubtitle')}
            </p>
          </div>
        </header>

        <form className="funding-modal__body t2i-case-form__body" onSubmit={handleSubmit}>
          <section className="t2i-case-form__section">
            <label className="t2i-case-form__label" htmlFor="t2v-case-name">
              {t('promptCase.name')}<span className="t2i-case-form__req" style={{ color: '#f87171' }}>*</span>
            </label>
            <input
              id="t2v-case-name"
              type="text"
              className="t2i-case-form__input"
              value={form.name}
              maxLength={128}
              disabled={saving}
              onChange={(e) => setField('name', e.target.value)}
              placeholder={t('promptCase.t2vNamePh')}
            />
          </section>

          <section className="t2i-case-form__section">
            <div className="t2i-case-form__label-row">
              <label className="t2i-case-form__label" htmlFor="t2v-case-prompt">
                {t('promptCase.t2vPrompt')}<span className="t2i-case-form__req" style={{ color: '#f87171' }}>*</span>
              </label>
              <span className={`t2i-case-form__count${promptLen >= VIDEO_T2V_PROMPT_MAX_LENGTH ? ' is-limit' : ''}`}>
                {promptLen}/{VIDEO_T2V_PROMPT_MAX_LENGTH}
              </span>
            </div>
            <textarea
              id="t2v-case-prompt"
              className="t2i-case-form__textarea"
              value={form.prompt}
              maxLength={VIDEO_T2V_PROMPT_MAX_LENGTH}
              disabled={saving}
              rows={5}
              onChange={(e) => setField('prompt', e.target.value)}
              placeholder={t('promptCase.t2vPromptPh')}
            />
          </section>

          <section className="t2i-case-form__section">
            <div className="t2i-case-form__label-row">
              <span className="t2i-case-form__label">{t('promptCase.quality')}</span>
              <span className="t2i-case-form__meta">{form.generationMode}</span>
            </div>
            <div className="t2i-case-form__chip-row" role="group" aria-label={t('promptCase.quality')}>
              {VIDEO_GENERATION_MODE_OPTIONS.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  className={`t2i-case-form__chip${form.generationMode === item.value ? ' is-active' : ''}`}
                  onClick={() => setField('generationMode', item.value)}
                  disabled={saving}
                  title={item.hint}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </section>

          <section className="t2i-case-form__section">
            <div className="t2i-case-form__label-row">
              <span className="t2i-case-form__label">{t('promptCase.videoRatio')}</span>
              <span className="t2i-case-form__meta">{form.aspectRatio}</span>
            </div>
            <div className="t2i-case-form__ratio-row" role="group" aria-label={t('promptCase.videoRatio')}>
              {VIDEO_ASPECT_RATIO_OPTIONS.map((item) => {
                const frameStyle = getVideoAspectRatioFrameStyle(item.value, 22);
                return (
                  <button
                    key={item.value}
                    type="button"
                    className={`t2i-case-form__ratio${form.aspectRatio === item.value ? ' is-active' : ''}`}
                    onClick={() => setField('aspectRatio', item.value)}
                    disabled={saving}
                    title={`${item.label} · ${item.hint}`}
                  >
                    <span className="t2i-case-form__ratio-frame" aria-hidden="true">
                      <span className="t2i-case-form__ratio-box" style={frameStyle} />
                    </span>
                    <span className="t2i-case-form__ratio-text">{item.label}</span>
                  </button>
                );
              })}
            </div>
          </section>

          <section className="t2i-case-form__section">
            <div className="t2i-case-form__label-row">
              <span className="t2i-case-form__label">{t('promptCase.videoDuration')}</span>
              <span className="t2i-case-form__meta">{safeDurationSec}s</span>
            </div>
            <div className="t2i-case-form__chip-row" role="group" aria-label={t('promptCase.videoDuration')}>
              {VIDEO_DURATION_PRESETS.map((sec) => (
                <button
                  key={sec}
                  type="button"
                  className={`t2i-case-form__chip${safeDurationSec === sec ? ' is-active' : ''}`}
                  onClick={() => setField('durationSec', sec)}
                  disabled={saving}
                >
                  {sec}s
                </button>
              ))}
            </div>
            <label className="t2i-case-form__hint" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
              <span>{t('promptCase.fineTune')}</span>
              <input
                type="range"
                min={VIDEO_DURATION_MIN}
                max={VIDEO_DURATION_MAX}
                step={1}
                value={safeDurationSec}
                disabled={saving}
                onChange={(e) => setField('durationSec', normalizeVideoDurationSec(e.target.value))}
                aria-label={t('promptCase.fineTuneAria')}
                style={{ flex: 1 }}
              />
              <span>{VIDEO_DURATION_MIN}–{VIDEO_DURATION_MAX}s</span>
            </label>
          </section>

          <section className="t2i-case-form__section">
            <div className="t2i-case-form__label-row">
              <span className="t2i-case-form__label">{t('promptCase.seed')}</span>
              <span className="t2i-case-form__meta">{safeSeed}</span>
            </div>
            <div className="t2i-case-form__seed-row">
              <input
                id="t2v-case-form-seed"
                type="number"
                className="t2i-case-form__input t2i-case-form__seed-input"
                min={VIDEO_SEED_MIN}
                max={VIDEO_SEED_MAX}
                step={1}
                inputMode="numeric"
                value={safeSeed}
                disabled={saving}
                onChange={handleSeedInputChange}
                aria-label={t('promptCase.seedInputAria')}
              />
              <button
                type="button"
                className="t2i-case-form__seed-random"
                onClick={() => setField('seed', createRandomVideoSeed())}
                disabled={saving}
                title={t('promptCase.setRandom')}
                aria-label={t('promptCase.setRandomSeed')}
              >
                <Dices size={16} strokeWidth={1.75} aria-hidden="true" />
                <span>{t('promptCase.random')}</span>
              </button>
            </div>
          </section>

          {error ? (
            <p className="t2i-case-form__error" role="alert">{error}</p>
          ) : null}

          <footer className="t2i-case-form__footer">
            <button
              type="button"
              className="funding-button funding-button--ghost t2i-case-form__btn"
              onClick={onClose}
              disabled={saving}
            >
              {t('promptCase.cancel')}
            </button>
            <button
              type="submit"
              className="funding-button funding-button--accent t2i-case-form__btn t2i-case-form__btn--primary"
              disabled={!canSubmit}
            >
              {saving
                ? t('promptCase.saving')
                : (isEdit ? t('promptCase.saveChanges') : t('promptCase.createTemplate'))}
            </button>
          </footer>
        </form>
      </div>
    </div>,
    document.body,
  );
}
